import { useState, useEffect } from "react";

// Derive a bare domain (example.com) from a domain or website URL.
function toDomain(domain, website) {
  let d = (domain || "").trim().toLowerCase();
  if (!d && website) {
    try {
      d = new URL(website.startsWith("http") ? website : `https://${website}`).hostname;
    } catch {
      d = website.replace(/^https?:\/\//, "").split("/")[0];
    }
  }
  return d.replace(/^www\./, "") || null;
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const SIZE = { sm: "h-7 w-7 text-[10px]", md: "h-9 w-9 text-xs", lg: "h-12 w-12 text-base" };

/**
 * Shows a company's logo looked up from its domain, with graceful fallback:
 * Clearbit logo -> Google favicon -> coloured initials monogram. Pure presentational, no network
 * calls beyond the <img> itself, so it never blocks rendering.
 */
export function CompanyLogo({ domain, website, name, size = "md", className = "", rounded = "rounded-md" }) {
  const d = toDomain(domain, website);
  const sources = d ? [`https://logo.clearbit.com/${d}?size=128`, `https://www.google.com/s2/favicons?sz=128&domain=${d}`] : [];
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [d]);

  const box = `${SIZE[size] || SIZE.md} ${rounded} shrink-0 border border-border overflow-hidden flex items-center justify-center bg-muted ${className}`;

  if (!d || idx >= sources.length) {
    return (
      <div className={`${box} font-semibold text-muted-foreground select-none`} data-testid="company-logo-fallback" aria-hidden="true">
        {initials(name)}
      </div>
    );
  }
  return (
    <div className={box} data-testid="company-logo">
      <img
        src={sources[idx]}
        alt={name ? `${name} logo` : "Company logo"}
        className="h-full w-full object-contain"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setIdx((i) => i + 1)}
      />
    </div>
  );
}
